import { QUESTIONNAIRE_TYPES } from "../../SupplierQuestionnaires/helpers";
import { DataTable } from "../../../components/DataTable";
import * as React from "react";
import { useGetOwnedSupplierQuestionnaires } from "../../../hooks/query";
import { QUESTIONNAIRE_STATUSES, showErrorToast, SUPPLIER_TYPES } from "../../../utils";
import { hideAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import {
  Accordion,
  AccordionButton, AccordionIcon,
  AccordionItem, AccordionPanel,
  Box,
  Button,
  Flex,
  Icon, Text,
  useToast
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { routes } from "../../../config";
import { FaEye, FaPen } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { mapSupplierQuestionnairesSummaryData } from "./helpers";
import { useAuth } from "../../../context";

function SupplierQuestionnairesSummary() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierDashboard.supplierQuestionnairesSummary"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, userPermissions } = useAuth();

  const {
    data: supplierQuestionnaires,
    isLoading
  } = useGetOwnedSupplierQuestionnaires("", {
    onError: error => error.response?.status !== 404 ?
      showErrorToast(toast, error.response?.data?.parameters?.message ?? error.message) : null,
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const getColumns = (isForCurrentUser) => {
    function handleAction(data) {
      const questionnaireId = data.id;
      if (data.type === QUESTIONNAIRE_TYPES.GHG_EMISSIONS ||
        data.type === QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN) {
        navigate(`${routes.supplierQuestionnaire}?id=${questionnaireId}`);
      } else {
        navigate(`${routes.supplierPhysicalRiskQuestionnaire}?id=${questionnaireId}`);
      }
    }

    const isSupplierViewer = isForCurrentUser && userPermissions.length === 1 &&
      userPermissions.includes(SUPPLIER_TYPES.SUPPLIER_VIEWER);

    return [
      {
        header: t("gridColumns.questionnaire"),
        accessorKey: "questionnaire",
        size: 50
      },
      {
        header: t("gridColumns.period"),
        accessorKey: "period",
        size: 20
      },
      {
        header: t("gridColumns.status"),
        accessorKey: "mappedStatus",
        size: 40
      },
      {
        header: t("gridColumns.endDate"),
        accessorKey: "endDate",
        size: 50
      },
      {
        header: <div style={{ textAlign: "left" }}><b>{t("gridColumns.actions")}</b></div>,
        accessorKey: "action",
        size: 40,
        cell: row => {
          const { row: { original: rowData } } = row;

          return <Button
            onClick={() => handleAction(rowData)}
            title={rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED || !isForCurrentUser || isSupplierViewer ?
              t("reviewQuestionnaire") : t("fillOutQuestionnaire")}
            backgroundColor={rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED || !isForCurrentUser || isSupplierViewer ?
              "esg_green" : "esg_red"}
            color={"white"}
            _hover={{
              backgroundColor: rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED || !isForCurrentUser || isSupplierViewer ?
                "green" : "red"
            }}
            fontSize={12}
            paddingInline={2}
          >
            <Flex gap={1} alignItems="center">
              <Icon
                as={rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED || !isForCurrentUser || isSupplierViewer ?
                  FaEye : FaPen}
                inlineSize="3"
                blockSize="3"
                color={"white"}
              />
              {rowData.status === QUESTIONNAIRE_STATUSES.FINALIZED || !isForCurrentUser || isSupplierViewer ?
                t("review") : t("fillOut")}
            </Flex>
          </Button>;
        }
      }
    ];
  };

  return (
    <Box height={"100vh"} overflow={"auto"}>
      <Accordion allowMultiple defaultIndex={[0, 1]} marginBottom="10">
        <AccordionItem>
          <AccordionButton>
            <Box as="span" flex="1" textAlign="left">
              <Text fontSize="xl" fontWeight="600">
                {globalTranslation("supplierQuestionnaires.questionnairesAssignedToYou")}
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel overflow="auto" padding={2}>
            <DataTable
              data={mapSupplierQuestionnairesSummaryData(supplierQuestionnaires, true, user?.sub, userPermissions)}
              columns={getColumns(true)}
              isLoading={isLoading}
              marginBlockEnd={4}
              queryPageSize={50}
              showPaginator={supplierQuestionnaires && supplierQuestionnaires.length > 50}
            />
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <AccordionButton>
            <Box as="span" flex="1" textAlign="left">
              <Text fontSize="xl" fontWeight="600">
                {globalTranslation("supplierQuestionnaires.questionnairesAssignedToColleagues")}
              </Text>
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel overflow="auto" padding={2}>
            <DataTable
              data={mapSupplierQuestionnairesSummaryData(supplierQuestionnaires, false, user?.sub, userPermissions)}
              columns={getColumns(false)}
              isLoading={isLoading}
              marginBlockEnd={4}
              queryPageSize={50}
              showPaginator={supplierQuestionnaires && supplierQuestionnaires.length > 50}
            />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </Box>
  );
}

export default SupplierQuestionnairesSummary;